import type { FractalChaosBandsLines } from "../../model/calcFractalChaosBands";
import type { FractalChaosBandsSettings } from "../../model";
import type { ZoomRange, CandleWithFlag } from "features/chart";
import { GRID, CANDLE_MIN_WIDTH } from "features/chart";

export function drawFractalChaosBands(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  data: CandleWithFlag[],
  zoom: ZoomRange,
  lines: FractalChaosBandsLines,
  settings: FractalChaosBandsSettings,
): void {
  const { left, right, top, bottom } = GRID;
  const chartWidth = width - left - right;
  const chartHeight = height - top - bottom;
  const { start, end } = zoom;
  const startIdx = Math.floor(start);
  const endIdx = Math.min(Math.floor(end), data.length - 1);
  const visibleCount = Math.floor(end) - startIdx + 1;
  const cw = Math.max(CANDLE_MIN_WIDTH, chartWidth / visibleCount);

  let minPrice = Infinity, maxPrice = -Infinity;
  for (let i = startIdx; i <= endIdx; i++) {
    const c = data[i];
    if (!c) continue;
    minPrice = Math.min(minPrice, c.low);
    maxPrice = Math.max(maxPrice, c.high);
  }
  if (minPrice === Infinity) return;
  const priceRange = maxPrice - minPrice || 1;
  const pad = priceRange * 0.05;
  minPrice -= pad;
  maxPrice += pad;
  const range = maxPrice - minPrice;
  const priceToY = (p: number) => top + ((maxPrice - p) / range) * chartHeight;
  const getX = (i: number) => left + (i - startIdx + 0.5) * cw;

  ctx.clearRect(0, 0, width, height);

  if (settings.background.visible) {
    ctx.save();
    ctx.globalAlpha = 0.1;
    ctx.fillStyle = settings.background.color;
    ctx.beginPath();
    let started = false;
    for (let i = startIdx; i <= endIdx; i++) {
      const v = lines.upper[i];
      if (v === null) continue;
      if (!started) { ctx.moveTo(getX(i), priceToY(v)); started = true; }
      else ctx.lineTo(getX(i), priceToY(v));
    }
    if (started) {
      for (let i = endIdx; i >= startIdx; i--) {
        const v = lines.lower[i];
        if (v === null) continue;
        ctx.lineTo(getX(i), priceToY(v));
      }
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  const drawLine = (values: (number | null)[], color: string) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.beginPath();
    let started = false;
    for (let i = startIdx; i <= endIdx; i++) {
      const v = values[i];
      if (v === null) { started = false; continue; }
      if (!started) { ctx.moveTo(getX(i), priceToY(v)); started = true; }
      else ctx.lineTo(getX(i), priceToY(v));
    }
    ctx.stroke();
  };

  if (settings.up.visible) drawLine(lines.upper, settings.up.color);
  if (settings.down.visible) drawLine(lines.lower, settings.down.color);
}
